import React from 'react';
import { motion } from 'framer-motion';
import { Sun, Fish, Salad, Flower2, Wheat, Leaf, Snowflake } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import { ZoomableImage } from './ZoomableImage';

const SAISONS = [
  { key: 'printemps', label: 'Printemps (Mars – Mai)', icon: Flower2, color: 'text-pink-500' },
  { key: 'ete', label: 'Été (Juin – Août)', icon: Sun, color: 'text-amber-500' },
  { key: 'automne', label: 'Automne (Septembre – Novembre)', icon: Wheat, color: 'text-orange-600' },
  { key: 'hiver', label: 'Hiver (Décembre – Février)', icon: Snowflake, color: 'text-sky-500' },
];

const Item = ({ dish, onAdd, accentClass = 'text-terracotta' }) => (
  <div
    onClick={() => onAdd(dish)}
    className="group py-4 px-5 -mx-5 rounded-2xl hover:bg-terracotta/5 transition-all cursor-pointer text-center"
    data-testid={`estivale-item-${dish.dish_id}`}
  >
    {dish.image_url && (
      <div className="mb-3 rounded-xl overflow-hidden max-w-md mx-auto">
        <ZoomableImage
          src={dish.image_url}
          alt={dish.name}
          className="w-full h-auto aspect-[16/9] object-cover"
          loading="lazy"
        />
      </div>
    )}
    <h5 className={`font-heading text-lg md:text-xl font-bold text-navy group-hover:${accentClass} transition-colors`}>
      {dish.name}
    </h5>
    <p className="text-navy/70 text-sm mt-2 italic leading-relaxed max-w-2xl mx-auto">
      {dish.description}
    </p>
    <p className="text-xs text-terracotta mt-3 opacity-0 group-hover:opacity-100 transition-opacity">
      + Ajouter au panier
    </p>
  </div>
);

const SectionTitle = ({ children, accent = 'text-terracotta' }) => (
  <div className="text-center mb-6">
    <h3 className={`font-heading text-2xl md:text-3xl font-bold ${accent} tracking-wide`}>
      {children}
    </h3>
    <div className="w-32 h-[2px] bg-terracotta mx-auto mt-3"></div>
  </div>
);

export const MenuCardCarteEstivale = ({ dishes }) => {
  const { addToCart } = useCart();

  const handleAddToCart = async (d) => {
    try {
      await addToCart(d.dish_id, 1);
      toast.success(`${d.name} ajouté au panier`);
    } catch {
      toast.error('Connectez-vous pour commander');
    }
  };

  const carpaccios = dishes.filter((d) => d.formule_type === 'carpaccio');
  const ceviches = dishes.filter((d) => d.formule_type === 'ceviche');
  const pokes = dishes.filter((d) => d.formule_type === 'poke');

  return (
    <div className="max-w-4xl mx-auto space-y-12">
      {/* Bannière */}
      <div className="bg-gradient-to-br from-[#E8B384] to-[#D88A5A] rounded-2xl px-8 py-10 text-center border-b-4 border-terracotta shadow-xl">
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center justify-center gap-3 mb-2">
            <span className="w-12 h-[1px] bg-white"></span>
            <Sun className="text-white" size={26} />
            <span className="w-12 h-[1px] bg-white"></span>
          </div>
          <h2 className="font-heading text-3xl md:text-5xl font-bold text-white tracking-wide">
            Carte Estivale
          </h2>
          <p className="text-white/90 mt-3 italic font-heading text-lg">
            Carpaccios saisonniers • Cevichés • Poké Bowls
          </p>
        </motion.div>
      </div>

      {/* CARPACCIOS — par saison */}
      {carpaccios.length > 0 && (
        <section className="bg-[#FAF8F4] rounded-3xl p-8 md:p-12 shadow-card border border-[#D4C4AA]/30">
          <SectionTitle>Carpaccios saisonniers</SectionTitle>

          {SAISONS.map(({ key, label, icon: Icon, color }) => {
            const items = carpaccios.filter((d) => d.mois === key);
            if (items.length === 0) return null;
            return (
              <div key={key} className="mb-10 last:mb-0">
                <div className="flex items-center justify-center gap-3 mb-5">
                  <span className="text-terracotta text-lg">—</span>
                  <Icon className={color} size={18} />
                  <h4 className="font-heading text-sm md:text-base font-bold text-terracotta tracking-[0.25em] uppercase">
                    {label}
                  </h4>
                  <Icon className={color} size={18} />
                  <span className="text-terracotta text-lg">—</span>
                </div>
                {items.map((dish) => (
                  <Item key={dish.dish_id} dish={dish} onAdd={handleAddToCart} />
                ))}
              </div>
            );
          })}
        </section>
      )}

      {/* CEVICHES */}
      {ceviches.length > 0 && (
        <section className="bg-[#FAF8F4] rounded-3xl p-8 md:p-12 shadow-card border border-[#D4C4AA]/30">
          <SectionTitle>Les Cevichés</SectionTitle>
          <p className="text-center text-navy/60 italic text-sm mb-8">L'Esprit Lima — préparés à la minute pour une fraîcheur absolue</p>
          <div className="flex items-center justify-center gap-3 mb-5">
            <span className="text-terracotta text-lg">—</span>
            <Fish className="text-sky-500" size={18} />
            <h4 className="font-heading text-sm font-bold text-terracotta tracking-[0.25em] uppercase">
              Accompagnés de patate douce, maïs chulpi & oignons rouges
            </h4>
            <Fish className="text-sky-500" size={18} />
            <span className="text-terracotta text-lg">—</span>
          </div>
          {ceviches.map((dish) => (
            <Item key={dish.dish_id} dish={dish} onAdd={handleAddToCart} />
          ))}
        </section>
      )}

      {/* POKE BOWLS */}
      {pokes.length > 0 && (
        <section className="bg-[#FAF8F4] rounded-3xl p-8 md:p-12 shadow-card border border-[#D4C4AA]/30">
          <SectionTitle>Les Poké Bowls</SectionTitle>
          <p className="text-center text-navy/60 italic text-sm mb-8">L'Escale Hawaïenne — Base au choix : Riz vinaigré, Quinoa bio ou Salade de jeunes pousses</p>
          <div className="flex items-center justify-center gap-3 mb-5">
            <span className="text-terracotta text-lg">—</span>
            <Salad className="text-emerald-600" size={18} />
            <h4 className="font-heading text-sm font-bold text-terracotta tracking-[0.25em] uppercase">
              Bols composés avec finesse
            </h4>
            <Leaf className="text-emerald-600" size={18} />
            <span className="text-terracotta text-lg">—</span>
          </div>
          {pokes.map((dish) => (
            <Item key={dish.dish_id} dish={dish} onAdd={handleAddToCart} />
          ))}
        </section>
      )}

      <div className="text-center">
        <p className="text-navy/60 text-sm italic">
          Cliquez sur un plat pour l'ajouter au panier
        </p>
      </div>
    </div>
  );
};
